/**
 * Cognito Hosted-UI login — OAuth authorization-code flow with PKCE.
 *
 * Enabled when the build carries VITE_AUTH_DOMAIN + VITE_AUTH_CLIENT_ID
 * (production; baked in as Docker build args). Without them the app falls back
 * to the dev-IdP picker and everything in this module is inert.
 *
 * Flow:
 *   beginLogin()          → generate PKCE verifier/challenge + state, stash in
 *                           sessionStorage, redirect to the Hosted UI.
 *   completeLogin(qs)     → validate state, exchange the code at /oauth2/token,
 *                           return {idToken, refreshToken, expiresIn}.
 *   refreshSession()      → mint a fresh ID token from the stored refresh
 *                           token and update the auth store in place.
 *   ensureFreshSession()  → router guard helper: true if a valid session
 *                           exists or could be silently refreshed.
 *
 * The API is called with the Cognito **ID token** (it carries `aud` = app
 * client id, which the API's AUTH_AUDIENCE check verifies; access tokens use
 * `client_id` instead and would fail that check).
 */

import { useAuth } from './auth-store.js';

const DOMAIN = (import.meta.env.VITE_AUTH_DOMAIN as string | undefined)?.replace(/\/$/, '');
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID as string | undefined;

const PKCE_KEY = 'kdental:pkce';

export function cognitoEnabled(): boolean {
  return Boolean(DOMAIN && CLIENT_ID);
}

function callbackUri(): string {
  return `${window.location.origin}/auth/callback`;
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return new Uint8Array(digest);
}

/** Kick off the Hosted-UI login (full-page redirect). */
export async function beginLogin(): Promise<void> {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  window.sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));

  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: callbackUri(),
    state,
    code_challenge: b64url(await sha256(verifier)),
    code_challenge_method: 'S256',
  });
  window.location.assign(`${DOMAIN}/oauth2/authorize?${params.toString()}`);
}

export interface CognitoTokens {
  idToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

/** Exchange the callback's ?code for tokens. Throws with a readable message. */
export async function completeLogin(search: URLSearchParams): Promise<CognitoTokens> {
  const error = search.get('error');
  if (error) {
    throw new Error(search.get('error_description') ?? `Sign-in failed (${error})`);
  }
  const code = search.get('code');
  if (!code) throw new Error('Sign-in callback is missing the authorization code.');

  const stashedRaw = window.sessionStorage.getItem(PKCE_KEY);
  window.sessionStorage.removeItem(PKCE_KEY);
  const stashed = stashedRaw ? (JSON.parse(stashedRaw) as { verifier: string; state: string }) : null;
  if (!stashed || search.get('state') !== stashed.state) {
    throw new Error('Sign-in state mismatch — start again from the login page.');
  }

  return tokenRequest({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID!,
    code,
    redirect_uri: callbackUri(),
    code_verifier: stashed.verifier,
  });
}

/**
 * Silently refresh the session from the stored refresh token. Returns true and
 * updates the auth store on success; false (leaving the store alone) otherwise.
 */
export async function refreshSession(): Promise<boolean> {
  const { refreshToken } = useAuth.getState();
  if (!cognitoEnabled() || !refreshToken) return false;
  try {
    const t = await tokenRequest({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID!,
      refresh_token: refreshToken,
    });
    // Refresh responses omit a new refresh token unless rotation is on — keep
    // the existing one in that case.
    useAuth.getState().setToken({
      token: t.idToken,
      expiresIn: t.expiresIn,
      refreshToken: t.refreshToken ?? refreshToken,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Router-guard helper: a usable session exists (valid token, or one we could
 * silently refresh). Never throws.
 */
export async function ensureFreshSession(): Promise<boolean> {
  const auth = useAuth.getState();
  if (auth.isAuthenticated()) return true;
  return refreshSession();
}

/** Hosted-UI logout URL (clears the Cognito session cookie, lands on /login). */
export function logoutUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    logout_uri: `${window.location.origin}/login`,
  });
  return `${DOMAIN}/logout?${params.toString()}`;
}

async function tokenRequest(fields: Record<string, string>): Promise<CognitoTokens> {
  const res = await fetch(`${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(fields).toString(),
  });
  const body = (await res.json().catch(() => null)) as {
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
    error?: string;
  } | null;
  if (!res.ok || !body?.id_token) {
    throw new Error(body?.error ?? `Token exchange failed (HTTP ${res.status})`);
  }
  return {
    idToken: body.id_token,
    refreshToken: body.refresh_token ?? null,
    expiresIn: body.expires_in ?? 3600,
  };
}
